import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, FileText, Hash, Calendar, User, Building2 } from 'lucide-react';
import { DocumentVerificationService } from '../services/DocumentVerificationService';
import type { PublicDocumentVerificationRecord } from '../utils/documentVerification';

const getDocumentTypeLabel = (documentType?: PublicDocumentVerificationRecord['documentType']): string => {
  switch (documentType) {
    case 'medical_record':
      return 'Evolução clínica';
    case 'medical_record_bundle':
      return 'Lote de evoluções clínicas';
    case 'assessment_result':
      return 'Avaliação clínica';
    case 'attendance_certificate':
      return 'Atestado de presença';
    default:
      return 'Documento clínico';
  }
};

const getPatientIdentifierLabel = (documentType?: PublicDocumentVerificationRecord['documentType']): string => {
  if (documentType === 'attendance_certificate') {
    return 'CPF / prontuário';
  }

  return 'Prontuário';
};

const PublicDocumentVerificationPage = () => {
  const { token, tenantSlug } = useParams<{ token: string; tenantSlug?: string }>();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [record, setRecord] = useState<PublicDocumentVerificationRecord | null>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        setErrorMessage(null);
        const resolved = await DocumentVerificationService.getPublicDocumentVerification(token || '', tenantSlug || null);
        if (!active) return;

        if (!resolved) {
          setRecord(null);
          setErrorMessage('Documento não encontrado para este código de verificação.');
          return;
        }

        setRecord(resolved);
      } catch (error: any) {
        if (!active) return;
        setRecord(null);
        setErrorMessage(error?.message || 'Não foi possível validar este documento agora.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [tenantSlug, token]);

  const isValid = record?.status === 'valid';
  const isFileVerification = record?.verificationScope === 'file';
  const title = isFileVerification ? 'Autenticidade do arquivo' : 'Autenticidade do documento';
  const description = isFileVerification
    ? 'Este painel confirma o PDF gerado no sistema e sua vinculação com o documento eletrônico assinado.'
    : 'Este painel confirma a integridade lógica do documento emitido eletronicamente no sistema.';

  return (
    <main className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100">
      <div className="mx-auto max-w-3xl">
        <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur">
          <div className="mb-8 flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-black uppercase tracking-[0.3em] text-cyan-300">Conferência Pública</div>
              <h1 className="mt-3 text-3xl font-black tracking-tight">{title}</h1>
              <p className="mt-3 max-w-2xl text-sm text-slate-300">
                {description}
              </p>
            </div>
            <div className={`inline-flex items-center rounded-full px-4 py-2 text-sm font-bold ${
              loading
                ? 'bg-slate-800 text-slate-200'
                : isValid
                  ? 'bg-emerald-500/15 text-emerald-200'
                  : 'bg-red-500/15 text-red-200'
            }`}>
              {loading ? (
                'Verificando'
              ) : isValid ? (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Documento válido
                </>
              ) : (
                <>
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  Documento invalidado
                </>
              )}
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6">
            {loading ? (
              <div className="py-10 text-center text-sm text-slate-300">Carregando dados de verificação...</div>
            ) : errorMessage ? (
              <div className="rounded-2xl border border-red-400/30 bg-red-500/10 p-5 text-sm text-red-100">
                {errorMessage}
              </div>
            ) : record ? (
              <div className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="mb-2 flex items-center text-sm font-semibold text-cyan-200">
                      <FileText className="mr-2 h-4 w-4" />
                      Documento
                    </div>
                    <div className="text-lg font-bold text-white">{record.documentTitle}</div>
                    <div className="mt-2 text-sm text-slate-300">
                      {getDocumentTypeLabel(record.documentType)}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="mb-2 flex items-center text-sm font-semibold text-cyan-200">
                      <Building2 className="mr-2 h-4 w-4" />
                      Tenant emissor
                    </div>
                    <div className="text-lg font-bold text-white">{record.tenantName || 'Tenant não informado'}</div>
                    <div className="mt-2 text-sm text-slate-300">{record.tenantSlug || 'slug não disponível'}</div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="mb-2 flex items-center text-sm font-semibold text-cyan-200">
                      <User className="mr-2 h-4 w-4" />
                      Paciente
                    </div>
                    <div className="text-base font-semibold text-white">{record.patientName || 'Paciente não informado'}</div>
                    <div className="mt-2 text-sm text-slate-300">
                      {getPatientIdentifierLabel(record.documentType)}: {record.patientMedicalRecordNumber || 'não informado'}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="mb-2 flex items-center text-sm font-semibold text-cyan-200">
                      <User className="mr-2 h-4 w-4" />
                      Profissional
                    </div>
                    <div className="text-base font-semibold text-white">{record.professionalName || 'Profissional não informado'}</div>
                    <div className="mt-2 text-sm text-slate-300">
                      {record.professionalRole || 'Função não informada'}
                      {record.professionalRegistration ? ` • ${record.professionalRegistration}` : ''}
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="mb-2 flex items-center text-sm font-semibold text-cyan-200">
                      <Calendar className="mr-2 h-4 w-4" />
                      Assinatura
                    </div>
                    <div className="text-base font-semibold text-white">
                      {new Date(record.signedAt).toLocaleString('pt-BR')}
                    </div>
                    <div className="mt-2 text-sm text-slate-300">
                      Versão do template: {record.templateVersion}
                    </div>
                    {record.generatedAt && (
                      <div className="mt-2 text-sm text-slate-300">
                        Arquivo gerado em: {new Date(record.generatedAt).toLocaleString('pt-BR')}
                        {record.generationChannel ? ` • ${record.generationChannel === 'download' ? 'download' : 'impressão'}` : ''}
                      </div>
                    )}
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="mb-2 flex items-center text-sm font-semibold text-cyan-200">
                      <Hash className="mr-2 h-4 w-4" />
                      {record.fileHash ? 'Hashes de verificação' : 'Hash do documento'}
                    </div>
                    {record.fileHash ? (
                      <div className="space-y-3">
                        <div>
                          <div className="mb-1 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                            Conteúdo assinado
                          </div>
                          <div className="break-all font-mono text-xs text-white">{record.documentHash}</div>
                        </div>
                        <div>
                          <div className="mb-1 text-[11px] uppercase tracking-[0.2em] text-slate-400">
                            Arquivo PDF
                          </div>
                          <div className="break-all font-mono text-xs text-white">{record.fileHash}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="break-all font-mono text-xs text-white">{record.documentHash}</div>
                    )}
                  </div>
                </div>

                <div className={`rounded-2xl border p-4 text-sm ${
                  isValid
                    ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-50'
                    : 'border-red-400/30 bg-red-500/10 text-red-50'
                }`}>
                  {isValid
                    ? isFileVerification
                      ? 'O arquivo PDF permanece válido e compatível com o documento eletrônico assinado.'
                      : 'O documento permanece válido e compatível com o registro eletrônico assinado.'
                    : isFileVerification
                      ? 'O arquivo PDF continua conferível, mas o documento clínico vinculado foi invalidado no prontuário eletrônico.'
                      : 'O documento foi invalidado no prontuário eletrônico. A conferência permanece disponível apenas para auditoria.'}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
};

export default PublicDocumentVerificationPage;
